import React, { useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import { POPUPS_REDUCER } from 'js/store/constants';
import { equals, findLastIndex, pick, pipe, prop, when } from 'ramda';
import { useUpdateEffect } from 'js/hooks/useUpdateEffect';
import { componentService } from 'js/services/component.service';
import { useCustomSelector, useUserSelector } from 'js/store/hooks';

import { Layer } from './Layer';
import * as hooks from './hooks';
import { navigateByIndexAction, navigateNextAction, navigatePrevAction } from './store/actions';

import style from './PopupManager.scss';

export const PopupManager = memo(() => {
    const { layerIds, layersMeta, enabled } = useCustomSelector(POPUPS_REDUCER, pick(['layerIds', 'layersMeta', 'enabled']));
    const lastVisibleIndex = pipe(
        findLastIndex(layerId => layersMeta[layerId].hideBelow),
        when(equals(-1), () => 0)
    )(layerIds);
    const { onPrev, onNext, onBulletClick } = usePopupsActions();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const closePopups = hooks.useSmartClosePopups();

    // TODO check if this is still needed at all
    useUpdateEffect(() => {
        if (!isLoggedIn) {
            // close only for logged out
            closePopups('logout');
        }
    }, [closePopups, isLoggedIn]);

    if (!enabled || !layerIds.length) {
        return null;
    }

    return (
        <div className={style.popups}>
            {layerIds.map((layerId, index) =>
                <Layer
                    key={layerId}
                    isLayerVisible={layersMeta[layerId].alwaysVisible || index >= lastVisibleIndex}
                    {...{
                        layerId,
                        onPrev,
                        onNext,
                        onBulletClick
                    }}
                />)}
        </div>
    );
});

export const usePopupsActions = () => {
    const dispatch = useDispatch();

    return {
        onPrev: useCallback(() => dispatch(navigatePrevAction()), [dispatch]),
        onNext: useCallback(() => dispatch(navigateNextAction()), [dispatch]),
        onBulletClick: useCallback(index => dispatch(navigateByIndexAction(index)), [dispatch])
    };
};

componentService.registerWebContentComponents({ PopupManager });
